import { TreeItem, TreeView } from "@mui/lab";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import { createContext, FC, useContext, useMemo } from "react";
import { FileColumn, FileEncryptionTemplateDto, ObfuscationDataType, ObfuscationRuleDto, ObfuscationRuleType } from "~/gen/api/encrypt";
import { OptionsData, FileDataType, MappingEditParams, Pack } from "./FileTemplateDetail.hooks";
import { JSONSchema4 } from "./jsonschema";
import { Autocomplete, Box, FormControl, FormHelperText, InputLabel, Link, MenuItem, Select, Stack, TextField as MuiTextField, Tooltip, Typography } from "@mui/material";
import { SecondaryClassificationResponseDto } from "~/gen/api/governance";
import SelectiveContextProvider from "~/controller/SelectiveContextProvider";
import { useHasPermit } from "~/authProvider/useHasPermit";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION } from "~/constants";
import { displayInferredType } from "~/components/settings/encryption/obfsRuleCompatibleMapping";



const JsonMappingViewer: FC<{
  record:FileEncryptionTemplateDto,
  optionsData:OptionsData,
  mappingEditParams:MappingEditParams,
  errors?: Record<string, object>|null,
}> = ({ record, optionsData, mappingEditParams, errors }) => {
  const hasWritePermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const treeData = useMemo(() => {
    const rootSchema : JSONSchema4 = JSON.parse(record.jsonSchema || '{}')
    const treeData = buildTreeData(rootSchema, record?.columns ?? [])

    sortTree(treeData, {count: 0})
    return treeData
  }, [record?.jsonSchema, record?.columns])
  const optionsDataCached = useMemo(() => {
    const ruleMap = {
      [FileDataType.String]: [...(optionsData.ruleMap[FileDataType.String] ?? []), ...(optionsData.ruleMap[FileDataType.Date] ?? [])],
      [FileDataType.Date]:   [...(optionsData.ruleMap[FileDataType.Date] ?? []), ...(optionsData.ruleMap[FileDataType.String] ?? [])],
      [FileDataType.Numeric]: optionsData.ruleMap[FileDataType.Numeric] ?? [],
    }
    const clearDataRule = optionsData.obfsRules.all.find(r => r.ruleType === ObfuscationRuleType.ClearData)
    const i_arr = [
      (optionsData.obfsRules.maps[ObfuscationDataType.String] ?? [])[Symbol.iterator](),
      (optionsData.obfsRules.maps[ObfuscationDataType.DateTime] ?? [])[Symbol.iterator](),
      (optionsData.obfsRules.maps[ObfuscationDataType.Integer] ?? [])[Symbol.iterator](),
      (optionsData.obfsRules.maps[ObfuscationDataType.Numeric] ?? [])[Symbol.iterator](),
    ].map(i => {
      if (clearDataRule) { i.next() }
      return [...i]
    })
    const h = clearDataRule ? [clearDataRule] : []
    const obfsRules = {
      ...optionsData.obfsRules,
      maps: {
        [ObfuscationDataType.String]: [...h, ...i_arr[0], ...i_arr[1]],
        [ObfuscationDataType.Numeric]: [...h, ...i_arr[2], ...i_arr[3]],
        [ObfuscationDataType.DateTime]: [...h, ...i_arr[1], ...i_arr[0]],
      } as Record<ObfuscationDataType, ObfuscationRuleDto[]>
    }
    return ({optionsData: {...optionsData, ruleMap, obfsRules}})
  }, [optionsData.classifications, optionsData.ruleMap, optionsData.obfsRules])
  
  const rootNames =
    treeData.parts.flatMap((s, i) => {
      if (i === 0) { return ['Json doc root'] }
      if (s === '[*]') {
        return [' > ', <Typography key={i} variant="caption" color="InactiveCaptionText"><i>items</i>[]</Typography>]
      } else {
        return [' > ', <Typography key={i} variant="body2" component="span" >{s}</Typography>]
      }
    })
  return (
    <OptionsDataContext.Provider value={optionsDataCached}>
      <MappingEditParamsContext.Provider value={{ listCtx: mappingEditParams.listCtx, valueLens: mappingEditParams.valueLens, errors }}>
        <TreeView
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
          sx={{
            mt: '-1px',
            '& div.MuiTreeItem-content': { 
              py: 1, 
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            },
          }}
          expanded={['$']}
        >
          <TreeItem nodeId="$" label={rootNames}>
            {JsonTreeView(treeData, !hasWritePermit)}
          </TreeItem>
        </TreeView>
      </MappingEditParamsContext.Provider>
    </OptionsDataContext.Provider>
  )
}

export default JsonMappingViewer;

const JsonTreeView = (tree: PathTree, disabled?:boolean) => {
  const useCtxFn = () => useContext(MappingEditParamsContext)
  return (
    <TreeView
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpandIcon={<ChevronRightIcon />}
    >
      {tree.children.map(c => {
        if (c.kind === 'leaf') {
          const leaf = c
          const selectorFn = (ctx: MappingEditParamsContextProps) => {
            return [
              ctx.valueLens.getValue(c.jpath || '', 'secondaryClassificationId'),
              // ctx.valueLens.getValue(c.jpath || '', 'dataType'),
              // ctx.valueLens.getValue(c.jpath || '', 'precision'),
              // ctx.valueLens.getValue(c.jpath || '', 'scale'),
              ctx.valueLens.getValue(c.jpath || '', 'encryptionRuleId'),
              ctx.valueLens.getValue(c.jpath || '', 'obfuscationRuleId'),
              c.jpath && ctx.errors && ctx.errors[c.jpath],
            ]
          }
          const notApplicable = !leaf.type || ((leaf.type !== 'string') && (leaf.type !== 'number') && (leaf.type !== 'integer'))
          const labelArr = 
            leaf.part === '[*]' ? [<Typography key={0} variant="caption" color="InactiveCaptionText"> <i>items</i>[]</Typography>]
            : leaf.part.split('[*]').reduce((acc, s, i, arr) => {
              if (s === '') {
                acc.push(<Typography key={i} variant="caption" color="InactiveCaptionText"> []</Typography>)
              } else {
                acc.push(s)
              }
              return acc
            }, [] as any[])
          return (
            <TreeItem nodeId={leaf.jpath || ''} key={leaf.jpath} 
              label={
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mr: 1 }}>
                  <Typography variant="subtitle2" sx={{ flex: notApplicable ? '1 1 0' : null }}>{labelArr}</Typography>
                  {notApplicable
                    ? <Box sx={{ flex: '4 4 0' }}>
                        <Typography variant="caption" color="warning.main">Not Applicable</Typography>
                      </Box>
                    : <SelectiveContextProvider
                        ProviderComponent={MappingEditParamsContext.Provider}
                        useContextFn={useCtxFn}
                        selector={selectorFn}
                      >
                        <JsonFieldMappingEditor jfield={leaf} disabled={disabled} />
                      </SelectiveContextProvider>
                  }
                </Stack>
              }
            >
            </TreeItem>
          )
        } else {
          const childTree = c
          const label = c.parts.reduce((acc, s, i) => {
            acc.push(s === '[*]' 
              ? <Typography key={i} variant="caption" color="InactiveCaptionText"> []</Typography>
              : (i > 0 ? '.' : '') + s)
            return acc
          }, [] as any[])
          return(
            <TreeItem nodeId={childTree.num+''} key={childTree.num} label={label}>
              {JsonTreeView(childTree, disabled)}
            </TreeItem>
          )
        }
      })}
    </TreeView>
  )
}

const JsonFieldMappingEditor : FC<{ jfield: PathLeaf, disabled?: boolean }> = ({
  jfield,
  disabled,
}) => {
  const { optionsData: { classifications, clssMap, ruleMap, obfsRules } } = useContext(OptionsDataContext)
  const { listCtx, valueLens, errors } = useContext(MappingEditParamsContext)
  const columnName = jfield.jpath || ''
  const d = listCtx.data[columnName]
  const columnError : any = errors && errors[columnName]
  const dataType = jfield.type === 'string' ? 
    (jfield.part === '$numberDecimal' ? FileDataType.Numeric : FileDataType.String)
    : FileDataType.Numeric
  const rules = 
    (dataType === FileDataType.Numeric ? 
      ((jfield.part === '$maxKey' || jfield.part === '$minKey') ? [] : ruleMap[FileDataType.Numeric])
    : (jfield.format === 'date' ? ruleMap[FileDataType.Date] 
      : (jfield.part === 'base64' && jfield.jpath?.endsWith(".$binary.base64")) || (jfield.part === 'subType' && jfield.jpath?.endsWith(".$binary.subType")) ? [] : ruleMap[FileDataType.String])
    )
    ?? []
  const encryptor = rules.find(r => r.id === valueLens.getValue(columnName, 'encryptionRuleId'))

  const classificationSelectOptions = useMemo(() => {
    return [
      { text: '[None]', value: null, },
      ...classifications,
    ]
  }, [classifications])

  const obfsRId   = valueLens.getValue(columnName, 'obfuscationRuleId')
  const currObfsRule = obfsRId ? obfsRules.all.find(r => r.ruleId === obfsRId) : undefined
  const oRules = 
      jfield.format === 'date'          ? obfsRules.maps[ObfuscationDataType.DateTime]
    : dataType === FileDataType.Numeric ? obfsRules.maps[ObfuscationDataType.Numeric]
    : obfsRules.maps[ObfuscationDataType.String]
  let error = (columnError && columnError['obfuscationRuleId']) || ''
  if (obfsRId && obfsRId !== 'skip') {
    error = !currObfsRule ? 'Rule has been deleted' : ''
  }

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: 800, ml: 'auto', my: 1, mr: 2 }}>
      <Box sx={{ width: 50 }}>
        <Typography variant="caption" color="InactiveCaptionText">Type: </Typography>
        <Typography variant="body2">{dataType === FileDataType.String ? 'String' : 'Number'}</Typography>
      </Box>
      <Box sx={{ width: 210 }}>
        <Autocomplete 
          disabled={disabled}
          disableClearable={true}
          options={classificationSelectOptions}
          getOptionLabel={(option) => option ? (option.text || '') : ''}
          value={clssMap[valueLens.getValue(columnName, 'secondaryClassificationId') || ''] || ''}
          
          renderInput={((params) =>
            <MuiTextField 
              label="Classification"
              {...params} 
              fullWidth 
              size="small" 
              error={Boolean(columnError && columnError['secondaryClassificationId'])}
              helperText={columnError && columnError['secondaryClassificationId']}
              {...{
                inputProps: {
                  ...params.inputProps,
                  'aria-label': 'Classification',
                }
              }}
            />
          )}
          onChange={(event, value, reason) => {
            if (!disabled && reason === 'selectOption') {
              if ((value as any)?.value === null) { // choosed '[None]'
                valueLens.setValues(columnName, {
                  secondaryClassificationId: undefined as any,
                  primaryClassification: undefined,
                  encryptionRuleId: 'skip' as any,
                  obfuscationRuleId: 'skip' as any,
                })
              } else {
                const cls1 = value as SecondaryClassificationResponseDto
                const prevpcls = valueLens.getValue(columnName, 'primaryClassification')
                const flip = prevpcls && !cls1.primaryClassification
                const patchVal = {
                  secondaryClassificationId: cls1.secondaryClassificationId,
                  primaryClassification: cls1.primaryClassification,
                  ...(flip && {
                    encryptionRuleId: 'skip' as any,
                    obfuscationRuleId: valueLens.getValue(columnName, 'obfuscationRuleId') ?? 'skip' as any,
                  })
                }
                valueLens.setValues(columnName, patchVal)
              }
            }
          }}
        />
      </Box>
      <Box sx={{ width: 200 }}>
        <FormControl fullWidth>
          <InputLabel id={`select-label-${columnName}`}>Encryption Rule</InputLabel>
          <Select
            disabled={disabled}
            label="Encryption Rule"
            labelId={`select-label-${columnName}`}
            id={`select-${columnName}`}
            error={Boolean(columnError && columnError['encryptionRuleId'])}
            value={valueLens.getValue(columnName, 'encryptionRuleId') || 'skip'}
            onChange={(e) => valueLens.setValue(columnName, 'encryptionRuleId', (e.target.value) as any)}
            size="small"
            sx={{ minWidth: 80 }}
          >
            <MenuItem value="skip">Skip</MenuItem>
            {rules.map(r => (
              <MenuItem key={r.id} value={r.id as any}>{r.name}</MenuItem>
            ))}
          </Select>
          {columnError && columnError['encryptionRuleId'] &&
            <FormHelperText error>{columnError && columnError['encryptionRuleId']}</FormHelperText>}
        </FormControl>
        {encryptor &&
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Typography variant="caption" sx={{ color: 'primary.light', '& span': { width: 44, display: 'inline-block' } }}>
              <span>Input: </span>{encryptor.sampleInput}
            </Typography>
            <Typography variant="caption" sx={{ color: 'success.main' }}>Output: {encryptor.sampleOutput}</Typography>
          </Box>}
      </Box>
      <Box sx={{ width: 200 }}>
        <FormControl fullWidth error={Boolean(error)}>
          <InputLabel id={`select-o-label-${columnName}`}>Obfuscation Rule</InputLabel>
          <Select
            disabled={disabled}
            label="Encryption Rule"
            labelId={`select-o-label-${columnName}`}
            id={`select-o-${columnName}`}
            error={Boolean(error)}
            value={valueLens.getValue(columnName, 'obfuscationRuleId') || 'skip'}
            onChange={(e) => valueLens.setValue(columnName, 'obfuscationRuleId', (e.target.value) as any)}
            size="small"
            sx={{ minWidth: 80 }}
          >
            <MenuItem value="skip">Skip</MenuItem>
            {error && currObfsRule && <MenuItem value={currObfsRule.ruleId}>{currObfsRule.ruleName}</MenuItem>}
            {oRules.map(r => (
              <MenuItem key={r.ruleId} value={r.ruleId} >{r.ruleName}</MenuItem>))}
          </Select>
          {error && <FormHelperText error>{error}</FormHelperText>}
          {!error && currObfsRule?.ruleType !== ObfuscationRuleType.ClearData &&
            <FormHelperText>{displayInferredType(currObfsRule?.inferredType as any ?? null)}</FormHelperText>}
          {currObfsRule?.description &&
            <Tooltip title={currObfsRule?.description}>
              <Link sx={{ ml:1.75, cursor:'pointer' }}><Typography component="p" variant="caption">Read Description</Typography></Link>
            </Tooltip>}
        </FormControl>
      </Box>
    </Stack>
  )
}

const OptionsDataContext = createContext({
  optionsData: {} as OptionsData,
})

interface MappingEditParamsContextProps {
  listCtx: {
      data: Record<string, FileColumn>;
      ids: string[];
  };
  valueLens: {
      getValue: (name: string, piece: keyof (Pack)) => string | number | null | undefined;
      setValue: (name: string, piece: keyof (Pack), value: number | string) => void;
      setValues: (name: string, newpack: Pack) => void;
  };
  errors: Record<string, object> | null | undefined;
}
const MappingEditParamsContext = createContext({
  listCtx: {} as {
    data: Record<string, FileColumn>,
    ids: string[],
  },  
  valueLens: {} as {
    getValue: (name: string, piece: keyof (Pack)) => string | number | null | undefined;
    setValue: (name: string, piece: keyof (Pack), value: number | string) => void;
    setValues: (name: string, newpack: Pack) => void;
  },
  errors: null as Record<string, object>|null|undefined,
} as MappingEditParamsContextProps)

interface PathToken {
  part: string,
  isLeaf: boolean
}

interface PathLeaf {
  kind: 'leaf',
  part: string,
  jpath?: string,
  type?: string,
  format?: string,
}

interface PathTree {
  kind: 'tree',
  parts: string[],
  children: (PathLeaf|PathTree)[],
  num?: number,
}

export const parsePath = (x:string): PathToken[] => {
  const tokens : PathToken[] = []
  if (!x.startsWith('$')) { throw 'invalid path' }
  tokens.push({ part: '$', isLeaf: false })
  let rest = x.substring(1)
  while (!tokens[tokens.length - 1].isLeaf) {
    if (rest.startsWith('[*].')) {
      const part = '[*]'
      rest = rest.substring(4)
      const isLeaf = rest.length === 0
      tokens.push({ part, isLeaf })
    } else if (rest.startsWith("['")) {
      const i = rest.indexOf("']")
      const part = rest.substring(2, i).replace(`\\'`, "'")
      rest = rest.substring(i + 2)
      const isLeaf = rest.length === 0
      tokens.push({ part, isLeaf })
    } else if (rest.startsWith('.')) {
      rest = rest.substring(1)
    }
    else {
      var i = [rest.indexOf('.', 1), rest.indexOf('[', 1)].sort((a, b) => a - b).find(n => n > -1);
      var isLeaf = i == null;
      var part = isLeaf ? rest : rest.substring(0, i);
      rest = isLeaf ? '' : rest.substring(i ?? 0);
      tokens.push({ part: part, isLeaf: isLeaf });
    }
  }
  return tokens
}

/**
 * 
 * @example
 * var srcJsonSchema = `{
 *   "$schema": "http://json-schema.org/draft-04/schema#",
 *   "type": "object",
 *   "properties": {
 *     "hehe": { "type": "integer" },
 *     "dob": { "type": "string", "format": "date" },
 *     "child1": { "$ref": "#/definitions/Child1" },
 *     "child2": { "$ref": "#/definitions/Child1" },
 *     "arr1": {
 *       "type": "array",
 *       "items": { "$ref": "#/definitions/Arr1" }
 *     },
 *     "arr2": {
 *       "type": "array",
 *       "items": { "$ref": "#/definitions/Arr2" }
 *     },
 *     "arr3": {
 *       "type": "array",
 *       "items": { "$ref": "#/definitions/Arr3" }
 *     }
 *   },
 *   "definitions": {
 *     "Child1": {
 *       "type": "object",
 *       "properties": {
 *         "name": { "type": "string" },
 *         "age": { "type": "integer" }
 *       }
 *     },
 *     "Arr1": { "type": "integer" },
 *     "Arr2": {
 *       "type": "object",
 *       "properties": {
 *         "num1": { "type": "integer" },
 *         "num2": { "type": "integer" }
 *       }
 *     },
 *     "Arr3": {
 *       "type": "object",
 *       "properties": {
 *         "name": { "type": "string" },
 *         "age": { "type": "integer" }
 *       }
 *     }
 *   }
 * }`
 *  
 * const testpaths = [
 *   "$[*].[*].hehe",
 *   "$[*].[*].dob",
 *   "$[*].[*].child1.name",
 *   "$[*].[*].child1.age",
 *   "$[*].[*].child2.name",
 *   "$[*].[*].child2.age"
 * ]
 * 
 * var theTree: PathTree = {
 *   kind: 'tree',
 *   parts: ['$'],
 *   children: [
 *     { kind: 'leaf', part: 'hehe' },
 *     { kind: 'leaf', part: 'dob' },
 *     {
 *       kind: 'tree',
 *       parts: ['child1'],
 *       children: [
 *         { kind: 'leaf', part: 'name' },
 *         { kind: 'leaf', part: 'age' },
 *       ]
 *     },
 *     {
 *       kind: 'tree',
 *       parts: ['child2'],
 *       children: [
 *         { kind: 'leaf', part: 'name' },
 *         { kind: 'leaf', part: 'age' },
 *       ]
 *     },
 *     { kind: 'leaf', part: 'arr1[*]' },
 *     {
 *       kind: 'tree',
 *       parts: ['arr2','[*]'],
 *       children: [
 *         { kind: 'leaf', part: 'num1' },
 *         { kind: 'leaf', part: 'num2' },
 *       ]
 *     },
 *     {
 *       kind: 'tree',
 *       parts: ['arr3','[*]'],
 *       children: [
 *         { kind: 'leaf', part: 'name' },
 *         { kind: 'leaf', part: 'age' },
 *       ]
 *     },
 *   ]
 * }
 * 
 */

const buildTreeData = (rootSchema:JSONSchema4, columns:FileColumn[]): PathTree => {
  const refMap = !rootSchema.definitions ? {} : Object.keys(rootSchema.definitions).reduce((acc, k) => {
    const defs = rootSchema.definitions as { [k: string]: JSONSchema4; }
    const refKey = `#/definitions/${k}`
    const defSchema = defs[k]
    acc[refKey] = defSchema
    return acc
  }, {} as Record<string, JSONSchema4>)
  const replaceRefSite = (schema:JSONSchema4) => {
    if (schema['$ref']) {
      const refName = schema['$ref']
      schema['actualSchema'] = refMap[refName]
      return
    }
    if (schema.type === 'object') {
      Object.values(schema.properties || {}).forEach(replaceRefSite)
      return
    } else if (schema.type === 'array') {
      const itemSchema =
        schema.items instanceof Array ? schema.items[0] : schema.items
      itemSchema && replaceRefSite(itemSchema)
    }
  }
  Object.values(refMap).forEach(replaceRefSite)
  replaceRefSite(rootSchema)
  const jpaths = columns.map(x => x.columnName as string)
  const pathPartsGroups : PathParts[] = jpaths.map(s => ({ jpath: s, parts: parsePath(s).map(x => x.part) }))

  const splitPartsGroups = (pathPartsGroups: PathParts[], depth: number) => {
    return pathPartsGroups.reduce((acc, group) => {
      const part = group.parts[depth]
      if (part) { 
        const arr = acc[part] || (acc[part] = [])
        arr.push(group)
      }
      return acc
    }, {} as Record<string, PathParts[]>)
  }
  const ensureScheme = (schema: JSONSchema4): JSONSchema4 => schema['$ref'] ? schema.actualSchema : schema
  const assembleTreeLeaf = (currTree:PathTree, schema:JSONSchema4, pathPartsGroups: PathParts[], depth: number) => {
    const splittedGroups = splitPartsGroups(pathPartsGroups, depth)
    const keys = Object.keys(splittedGroups)
    
    const getNextTree = (part:string): PathTree => {
      if (keys.length === 1) {
        currTree.parts.push(part)
        return currTree
      } else {
        const next : PathTree = { kind: 'tree', parts: [part], children: [] }
        currTree.children.push(next)
        return next
      }
    }
    const getNextSchema = (part:string, currSchema?:JSONSchema4): JSONSchema4|undefined => {
      currSchema = currSchema ?? schema
      if (currSchema.type === 'object') {
        const next = (currSchema.properties || {})[part]
        return next && ensureScheme(next)
      } else if (currSchema.type === 'array') {
        return currSchema.items && ensureScheme(currSchema.items instanceof Array ? currSchema.items[0] : currSchema.items)
      } else {
        return currSchema
      }
    }

    for(const key of keys) {
      let part = key
      const partsGroup = splittedGroups[key]
      const firstGroupParts = partsGroup[0].parts
      let nextSchema = getNextSchema(part)
      if (!nextSchema) { continue; }
      if (partsGroup.length > 1 
      || (depth < firstGroupParts.length - 1 // not leaf
        && ((firstGroupParts[firstGroupParts.length - 1] !== '[*]') // allow: **.bar; deny: **.[*]
        || (depth < firstGroupParts.length - 2 || firstGroupParts[firstGroupParts.length - 2] === '[*]'))) // allow: **.[*][*]; deny: **.foo[*]
      ) {
        const nextTree : PathTree = getNextTree(part)
        assembleTreeLeaf(nextTree, nextSchema, partsGroup, depth + 1)
        continue;
      } 
      if (nextSchema.type === 'array') {
        nextSchema = getNextSchema('[*]', nextSchema)
        part = key+'[*]'
        if (!nextSchema) { continue }
      } 
      const leaf : PathLeaf = { kind: 'leaf', part: part, jpath: partsGroup[0].jpath, type: nextSchema.type as any, format: nextSchema.format }
      currTree.children.push(leaf)
    }
  }

  const rootTree : PathTree = { kind: 'tree', parts: ['$'], children: [] }
  assembleTreeLeaf(rootTree, rootSchema, pathPartsGroups, 1)
  return rootTree
}

interface PathParts {
  jpath: string,
  parts: string[],
}

const sortTree = (t: PathTree, h: {count:number}) => {
  t.num = h.count
  h.count = h.count + 1
  t.children = innerSort(t.children, h)
  t.children.filter(c => c.kind === 'tree').forEach(c => {
    (c as PathTree).num = h.count
    h.count = h.count + 1
  })
}

const innerSort = (arr: (PathLeaf|PathTree)[], h: {count:number}) => {
  return arr.sort((a, b) => {
    if (a.kind === 'leaf') {
      if (b.kind === 'tree') {
        sortTree(b, h)
        return -1
      } else {
        return (a.jpath || '') < (b.jpath || '') ? -1 : 1
      }
    } else {
      sortTree(a, h)
      if (b.kind === 'leaf') {
        return 1
      } else {
        sortTree(b, h)
        return a.parts[a.parts.length - 1] < b.parts[b.parts.length - 1] ? -1 : 1
      }
    }
  })
}
